import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { OpenLink } from "../../components/ui";
import { hideChatAndFastTrack } from "../../lib/featureFlags";
import type { AgentBaseModelReference, AgentModel, AgentRun } from "../api/e2e";
import { shortStamp } from "../lib/format";
import {
  buildAgentModelLineage,
  buildAgentModelLineageTree,
  pendingModelLineageId,
  type AgentModelLineageEntry,
  type AgentModelLineageTreeNode,
} from "../lib/modelLineage";
import { companyRunPath } from "../lib/runRoutes";
import AgentAttributionLabel from "./AgentAttribution";
import {
  AgentInputModeBadge,
  AgentRunTrackBadge,
  AgentVisibilityBadge,
} from "./AgentRunProfileBadges";
import AgentStatusPill from "./AgentStatusPill";
import HypothesisHoverCard from "./HypothesisHoverCard";

interface PublishedHistoryItem {
  kind: "published";
  id: string;
  base_model: AgentBaseModelReference | null;
  model: AgentModel;
}

interface PendingHistoryItem {
  kind: "pending";
  id: string;
  base_model: AgentBaseModelReference | null;
  run: AgentRun;
}

type HistoryItem = PublishedHistoryItem | PendingHistoryItem;

function iterationsLabel(count: number): string {
  return `${count} direct ${count === 1 ? "iteration" : "iterations"}`;
}

function BranchIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M4 2.5v5A2.5 2.5 0 0 0 6.5 10H12" />
      <path d="m9.5 7.5 2.5 2.5-2.5 2.5" />
    </svg>
  );
}

function HistoryDatum({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="model-history-card__datum">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function HistoryRelationship({
  companyId,
  parent,
  directIterations,
}: {
  companyId: string;
  parent: AgentBaseModelReference | null;
  directIterations: number;
}) {
  const parentPath = parent
    ? `/companies/${encodeURIComponent(companyId)}/models/${encodeURIComponent(parent.model_version_id)}`
    : null;
  return (
    <div className="model-history-card__relationship">
      <BranchIcon />
      {parent && parentPath ? (
        <span>
          Based on <Link to={parentPath}>version {parent.sequence}</Link>
        </span>
      ) : (
        <span>Fresh model</span>
      )}
      {directIterations > 0 && (
        <span className="model-history-card__branches">
          {iterationsLabel(directIterations)}
        </span>
      )}
    </div>
  );
}

function IterationRequest({ value }: { value: string | null }) {
  const change = value?.trim() ?? "";
  if (!change) return null;
  return (
    <div className="model-history-card__change" title={change}>
      <span>Iteration request</span>
      <p>{change}</p>
    </div>
  );
}

function PublishedModelCard({
  companyId,
  model,
  lineage,
  latest,
}: {
  companyId: string;
  model: AgentModel;
  lineage: AgentModelLineageEntry;
  latest: boolean;
}) {
  const restrictedProductionUi = hideChatAndFastTrack();
  const modelPath = `/companies/${encodeURIComponent(companyId)}/models/${encodeURIComponent(model.id)}`;
  const relationship = model.base_model
    ? `iteration of version ${model.base_model.sequence}`
    : "fresh model";

  return (
    <article
      className={`model-history-card${latest ? " model-history-card--latest" : ""}`}
      data-depth={lineage.depth}
      data-model-version-id={model.id}
      aria-label={`Version ${model.sequence}, ${relationship}`}
    >
      <span className="model-history-card__marker" aria-hidden="true" />
      <div className="model-history-card__identity">
        <div className="model-history-card__heading">
          <Link to={modelPath}>Version {model.sequence}</Link>
          {latest && <span className="model-history-card__latest">Latest</span>}
          <AgentVisibilityBadge visibility={model.visibility} />
          <HypothesisHoverCard
            label={`View version ${model.sequence} modeling hypothesis`}
            eyebrow={`${model.ticker} v${model.sequence} · Modeling hypothesis`}
            hypothesis={model.hypothesis}
          />
        </div>
        <HistoryRelationship
          companyId={companyId}
          parent={model.base_model}
          directIterations={lineage.directIterations}
        />
        <IterationRequest value={model.iteration_message} />
      </div>
      <dl className="model-history-card__metadata">
        <HistoryDatum label="Type">
          <AgentInputModeBadge mode={model.input_mode} />
        </HistoryDatum>
        {!restrictedProductionUi && (
          <HistoryDatum label="Track">
            <AgentRunTrackBadge fast={model.fast} />
          </HistoryDatum>
        )}
        <HistoryDatum label="Published">
          <span className="model-history-card__stamp">
            {shortStamp(model.published_at)}
          </span>
        </HistoryDatum>
        <HistoryDatum label="Generated by">
          <AgentAttributionLabel
            attribution={model.generated_by}
            showChannel={false}
          />
        </HistoryDatum>
      </dl>
      <OpenLink
        to={modelPath}
        label={`Open model version ${model.sequence}`}
        className="model-history-card__open"
      />
    </article>
  );
}

function PendingModelCard({
  run,
  lineage,
}: {
  run: AgentRun;
  lineage: AgentModelLineageEntry;
}) {
  const restrictedProductionUi = hideChatAndFastTrack();
  const relationship = run.base_model
    ? `iteration of version ${run.base_model.sequence}`
    : "fresh model";
  const title = run.base_model ? "Building next version" : "Building new model";
  const runPath = companyRunPath(run);

  return (
    <article
      className="model-history-card model-history-card--pending"
      data-depth={lineage.depth}
      data-pending-run-id={run.id}
      aria-label={`Pending model, ${relationship}`}
    >
      <span className="model-history-card__marker" aria-hidden="true" />
      <div className="model-history-card__identity">
        <div className="model-history-card__heading">
          <Link to={runPath}>{title}</Link>
          <AgentStatusPill state={run.state} />
          <HypothesisHoverCard
            label={`View pending ${run.ticker} modeling hypothesis`}
            eyebrow={`${run.ticker} · Pending model hypothesis`}
            hypothesis={run.hypothesis}
          />
        </div>
        <HistoryRelationship
          companyId={run.company_id}
          parent={run.base_model}
          directIterations={lineage.directIterations}
        />
        <IterationRequest value={run.iteration_message} />
      </div>
      <dl className="model-history-card__metadata">
        <HistoryDatum label="Type">
          <AgentInputModeBadge mode={run.parameters.input_mode} />
        </HistoryDatum>
        {!restrictedProductionUi && (
          <HistoryDatum label="Track">
            <AgentRunTrackBadge
              fast={run.parameters.run_mode === "fast_track"}
            />
          </HistoryDatum>
        )}
        <HistoryDatum label="Started">
          <span className="model-history-card__stamp">
            {shortStamp(run.created_at)}
          </span>
        </HistoryDatum>
        <HistoryDatum label="Started by">
          <AgentAttributionLabel
            attribution={run.started_by}
            showChannel={false}
          />
        </HistoryDatum>
      </dl>
      <OpenLink
        to={runPath}
        label={`Open pending ${run.ticker} model run`}
        className="model-history-card__open"
      />
    </article>
  );
}

function HistoryBranch({
  node,
  items,
  lineage,
  latestSequence,
  companyId,
}: {
  node: AgentModelLineageTreeNode;
  items: ReadonlyMap<string, HistoryItem>;
  lineage: ReadonlyMap<string, AgentModelLineageEntry>;
  latestSequence: number;
  companyId: string;
}) {
  const item = items.get(node.id);
  if (!item) return null;
  const itemLineage = lineage.get(node.id) ?? {
    depth: item.base_model ? 1 : 0,
    directIterations: node.children.length,
  };

  return (
    <li
      className={`model-history-branch${node.children.length > 0 ? " model-history-branch--parent" : ""}`}
      role="treeitem"
      aria-level={itemLineage.depth + 1}
    >
      <div className="model-history-branch__node">
        {item.kind === "published" ? (
          <PublishedModelCard
            companyId={companyId}
            model={item.model}
            lineage={itemLineage}
            latest={item.model.sequence === latestSequence}
          />
        ) : (
          <PendingModelCard run={item.run} lineage={itemLineage} />
        )}
      </div>
      {node.children.length > 0 && (
        <ol className="model-history-branch__children" role="group">
          {node.children.map((child) => (
            <HistoryBranch
              key={child.id}
              node={child}
              items={items}
              lineage={lineage}
              latestSequence={latestSequence}
              companyId={companyId}
            />
          ))}
        </ol>
      )}
    </li>
  );
}

export default function AgentModelHistory({
  companyId,
  models,
  pendingRuns,
}: {
  companyId: string;
  models: readonly AgentModel[];
  pendingRuns: readonly AgentRun[];
}) {
  const items: HistoryItem[] = [
    ...pendingRuns.map(
      (run): PendingHistoryItem => ({
        kind: "pending",
        id: pendingModelLineageId(run.id),
        base_model: run.base_model,
        run,
      }),
    ),
    ...models.map(
      (model): PublishedHistoryItem => ({
        kind: "published",
        id: model.id,
        base_model: model.base_model,
        model,
      }),
    ),
  ];
  const itemsById = new Map(items.map((item) => [item.id, item]));
  const lineage = buildAgentModelLineage(items);
  const tree = buildAgentModelLineageTree(items);
  const latestSequence = Math.max(0, ...models.map((model) => model.sequence));

  return (
    <div className="model-history-tree">
      <header className="model-history-tree__header">
        <div className="model-history-tree__intro">
          <span>Model lineage</span>
          <small>Iterations branch from their exact starting version.</small>
        </div>
        <div className="model-history-tree__legend" aria-label="Status legend">
          <span>
            <i className="model-history-tree__legend-dot model-history-tree__legend-dot--latest" />
            Latest
          </span>
          <span>
            <i className="model-history-tree__legend-dot model-history-tree__legend-dot--pending" />
            Building
          </span>
          <span>
            <i className="model-history-tree__legend-dot" />
            Published
          </span>
        </div>
      </header>
      <ol className="model-history-tree__forest" role="tree">
        {tree.map((node) => (
          <HistoryBranch
            key={node.id}
            node={node}
            items={itemsById}
            lineage={lineage}
            latestSequence={latestSequence}
            companyId={companyId}
          />
        ))}
      </ol>
    </div>
  );
}
